import type { Location, Order, Ship } from '../../graphql';
import type { ScenarioLocation, ScenarioOrder } from '../../lib/planningData';
import type { ClientId } from '../core/ClientId';
import type { Leg, EventsAndLegs, CargoEvent } from '../core/TimeData';
import {
  isTimeData,
  isSingleEventTimeData,
  CoreEventType,
  isLoadEvent,
  isLeg,
  isCargoEventTimeData,
  isTCOrderEventTimeData,
} from '../core/TimeData';
import type { LocationId, PortCallData } from '../core/Core';
import type { TCOrderEntry } from '@seaber/common/core/entries';
import type { EventTimeline } from '../core/Timelines';

export type ShipmentModalTab =
  | 'documents'
  | 'shippingPlan'
  | 'spotContract'
  | 'coa'
  | 'changelog'
  | 'notifications'
  | 'financials';
export interface ShipmentModalNavTarget {
  tabName: ShipmentModalTab;
  portLocation: string;
  section?: string;
}
export interface ShipmentOrder {
  orderId: Order['id'];
  startDate: Date;
  endDate: Date;
}

export interface ShipmentEvent {
  type: 'LOAD' | 'DISCHARGE';
  orderId: Order['id'];
  locationId: Location['id'];
  startDate: Date;
}

export interface ShipmentLeg {
  endLocationId: Location['id'];
  endDate: Date;
  startLocationId: Location['id'];
  startDate: Date;
}

export interface Shipment {
  vesselId: Ship['id'];
  orders: ShipmentOrder[];
  events: ShipmentEvent[];
  legs: ShipmentLeg[];
}

export function getCarriedOrdersByPortCall(portCalls: PortCallData[]): {
  scheduledOrderByOrderId: Map<number, TCOrderEntry>;
  carriedOrdersByPortCall: Map<ClientId | null, Set<ScenarioOrder>>;
} {
  const scheduledOrderByOrderId = new Map<number, TCOrderEntry>();
  const carriedOrdersByPortCall = new Map<
    ClientId | null,
    Set<ScenarioOrder>
  >();

  const carried = new Set<ScenarioOrder>();

  for (const pc of portCalls) {
    pc.tds?.forEach((td) => {
      if (td.event.type === CoreEventType.Discharge) {
        const order = td.event.order;
        carried.delete(order);
      }
    });

    const orders = new Set<ScenarioOrder>(carried);
    carriedOrdersByPortCall.set(pc.id, orders);

    pc.tds?.forEach((td) => {
      if (td.event.type === CoreEventType.Load) {
        const order = td.event.order;
        carried.add(order);
        if (isTCOrderEventTimeData(td)) {
          scheduledOrderByOrderId.set(order.id, td.event.entry);
        }
      } else if (td.event.type === CoreEventType.Discharge) {
        // load and discharge in same port call
        const order = td.event.order;
        carried.delete(order);
      }
    });
  }

  return { carriedOrdersByPortCall, scheduledOrderByOrderId };
}

export function getLoadedOrders(timeline: EventTimeline): {
  orders: ScenarioOrder[];
  plannedAmountByOrderId: Map<number, { mass: number; volume: number }>;
} {
  const loadEvents = timeline.voyageGroups.flatMap((vg) =>
    vg.events
      .filter(isCargoEventTimeData)
      .filter((td) => isLoadEvent(td.event)),
  );

  const orders = loadEvents.map((td) => td.event.order);

  const plannedAmountByOrderId = new Map<
    number,
    { mass: number; volume: number }
  >(
    loadEvents.map((td) => [
      td.event.order.id,
      { mass: td.mass, volume: td.volume },
    ]),
  );

  return { orders, plannedAmountByOrderId };
}

export const getTimelinePortCalls = (timeline: EventTimeline) => {
  const locationById = new Map<LocationId, ScenarioLocation>();

  const portCalls: PortCallData[] = [];
  const legs: Leg[] = [];

  timeline.voyageGroups.forEach((vg) => {
    vg.orders.forEach((order) => {
      if (order.locationByDischargePortId) {
        locationById.set(
          order.dischargePortId,
          order.locationByDischargePortId,
        );
      }
      if (typeof order.loadPortId === 'number' && order.locationByLoadPortId) {
        locationById.set(order.loadPortId, order.locationByLoadPortId);
      }
    });

    vg.events.forEach((td) => {
      if (isSingleEventTimeData(td)) {
        locationById.set(td.event.locationId, td.event.location);
      }
    });

    portCalls.push(...vg.portCalls);
    legs.push(...vg.legs);
  });

  return { legs, locationById, portCalls };
};

/**
 * Tries it's best to group given timedatas by shipment.
 *
 * Assumes that any non-cargo events at the start of input array belong to the first
 * encountered shipment and non-cargo events at the end of input array belong to the
 * last encountered shipment.
 */
export function groupTimeDatasByShipment(tds: EventsAndLegs) {
  /** Timedatas not yet associated with any shipment */
  let pendingTds: EventsAndLegs = [];

  const tdsByShipment = new Map<number | ClientId, EventsAndLegs>();

  let currentShipmentId: number | ClientId | null = null;

  for (const td of tds) {
    if (isTimeData(td) && isCargoEventTimeData(td)) {
      const shipmentId = getEventShipmentId(td.event);

      /** At this point all cargo events should be associated with a shipment, let's not guess if this is not the case */
      if (!shipmentId) {
        console.error(
          'groupTimeDatasByShipment encountered a cargo event with no associated shipment',
        );
        continue;
      }

      if (currentShipmentId !== shipmentId) {
        currentShipmentId = shipmentId;

        /** If any pending timedatas, assume they belong to this shipment */
        tdsByShipment.set(currentShipmentId, [
          ...(tdsByShipment.get(currentShipmentId) ?? []),
          ...pendingTds,
        ]);

        pendingTds = [];
      }

      tdsByShipment.set(shipmentId, [
        ...(tdsByShipment.get(shipmentId) ?? []),
        td,
      ]);
    } else {
      /** A ballast leg means that any previous shipment might have concluded */
      if (isLeg(td) && !td.laden) {
        currentShipmentId = null;
      }

      if (currentShipmentId) {
        tdsByShipment.set(currentShipmentId, [
          ...(tdsByShipment.get(currentShipmentId) ?? []),
          td,
        ]);
      } else {
        /** If no shipment id has been encountered yet, let's wait until we find one */
        pendingTds.push(td);
      }
    }
  }

  return tdsByShipment;
}

/** Prefers shipmentId from entry over order, because this allows a scenario (or suggestion) to override what is persisted into order.
 * Usually these are the same. At the time of writing, only suggestions make a difference. */
export function getEventShipmentId(event: CargoEvent): ClientId | null {
  return 'shipmentId' in event.entry
    ? event.entry.shipmentId
    : event.order.shipmentId;
}
